import { BaseView, PromiseCompletionSource } from "scene-manager";
import { ICanvasScaler } from "../../../canvas/ICanvasScaler";
import { Scales } from "../../../canvas/CanvasScaler";
import { Container, Graphics, IDestroyOptions, Ticker } from "pixi.js";
import { GlowFilter } from "pixi-filters";

export abstract class AdaptiveViewDrop<TResult> extends BaseView<TResult> {
    protected frame: Container;
    protected circle: Graphics;
    protected circleGlow: GlowFilter;
    protected rect: Graphics;
    protected rectGlow: GlowFilter;
    protected phase: "drop" | "morph" | "reveal" | "done" = "drop";
    protected time = 0;

    public constructor(
        protected readonly canvasScaler: ICanvasScaler,
        operationControls: PromiseCompletionSource<TResult>
    ) {
        super(operationControls);
        this.canvasScaler.onResize.add(this.onResize, this);
        this.circleGlow = new GlowFilter({distance: 20, outerStrength: 4, innerStrength: 0, quality: 0.5});
        this.rectGlow = new GlowFilter({distance: 10, outerStrength: 4, innerStrength: 0, quality: 0.5});
        this.rectGlow.padding = 10;
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.canvasScaler.onResize.remove(this.onResize, this);
        super.destroy(options);
    }
    
    protected abstract layout(width: number, height: number, initialLayout: boolean): void;

    protected easeInQuart(t: number) {
        return t * t * t * t;
    }

    private onResize(scales: Scales): void {
        this.layout(scales.width, scales.height, false);
    }

    protected build(): void {
        // Circle start
        this.circle = new Graphics()
            .beginFill("#ffffff")
            .drawCircle(0, 0, 40)
            .endFill();
        this.addChild(this.circle);
        this.circle.filters = [this.circleGlow];

        // Rectangle placeholder (hidden first)
        this.rect = new Graphics()
            .beginFill("#ffffff")
            .drawRoundedRect(
                -this.frame.width / 2,
                -this.frame.height / 2,
                this.frame.width,
                this.frame.height,
                20
            )
            .endFill();
        this.rect.filters = [this.rectGlow];
        this.rect.alpha = 0;
        this.addChild(this.rect);
    }

    public async show(): Promise<void> {
        return new Promise(resolve => {
            this.interactiveChildren = false;
            this.phase = "drop";
            this.time = 0;

            const rectWidth = this.frame.width + 200;
            const rectHeight = this.frame.height + 60;
            const rectX = this.frame.x - 100;
            const rectBottom = this.frame.y + this.frame.height;

            const dropDurationMS = 500;
            const morphDurationMS = 300;
            const revealDurationMS = 100;

            const update = (_dt: number) => {
                this.time += Ticker.shared.elapsedMS;

                if (this.phase === "drop") {
                    const targetY = this.frame.y + this.frame.height;
                    const progress = Math.min(this.time / dropDurationMS, 1);
                    const eased = this.easeInQuart(progress);
                    this.circle.y = -40 + (targetY + 40) * eased;

                    if (progress >= 1) {
                        this.phase = "morph";
                        this.time = 0;
                        this.circle.visible = false;
                        this.rect.alpha = 1;
                        this.rect.clear();
                        this.rect.beginFill("#ffffff");
                    }
                }

                else if (this.phase === "morph") {
                    const progress = Math.min(this.time / morphDurationMS, 1);
                    const currentHeight = rectHeight * progress;
                    const currentY = rectBottom - currentHeight;

                    this.rect.clear();
                    this.rect.beginFill("#ffffff");
                    this.rect.drawRoundedRect(rectX, currentY, rectWidth, currentHeight, 20);
                    this.rect.endFill();
                    if (progress >= 1) {
                        this.phase = "reveal";
                        this.time = 0;
                    }
                }

                else if (this.phase === "reveal") {
                    const progress = Math.min(this.time / revealDurationMS, 1);

                    this.rect.alpha = 1 - progress;
                    this.frame.alpha = progress;

                    if (progress >= 1) {
                        this.phase = "done";
                        Ticker.shared.remove(update, this);
                        this.removeChild(this.rect);
                        this.removeChild(this.circle);
                        this.rect.destroy();
                        this.circle.destroy();
                        this.rect = null;
                        this.circle = null;
                        this.interactiveChildren = true;
                        resolve();
                    }
                }
            };

            Ticker.shared.add(update, this);
        });
    }

    public async hide(): Promise<void> {

    }

}
